import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { View, Image, Pressable, ScrollView, Keyboard, Platform, StyleSheet } from 'react-native';
import useCustomTheme from '../../../hooks/theme-hook';
import Label from '../../common/Label';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch } from '../../../hooks/store-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import useTranslation from '../../../hooks/translation-hook';
import Loading from '../../common/Loading';

export interface ProfileModalProps {
  onConfirm?(): void;
  onClose?(): void;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  children?: ReactElement;
  showCancel?: boolean;
  onCancel?(): void;
  showDelete?: boolean;
  onDelete?(): void;
  width?: number;
  hideConfirm?: boolean;
  maxPageNumber?: number;
  pageNumber?: number;
  height?: number;
  hideCloseButton?: boolean;
  titleInfoButton?: ReactElement;
  confirmText?: string;
  cancelText?: string;
}

const ProfileModal = (props: ProfileModalProps) => {
  const isWeb = Platform.OS === 'web';
  const theme = useCustomTheme();
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const isDarkMode = !!theme.isDark;
  const cancelButtonColor = isDarkMode ? theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA' : theme.background.darkTurquoise;
  const scrollViewRef = useRef<ScrollView | null>(null);
  const contentHeightRef = useRef(0);
  const layoutHeightRef = useRef(0);
  const [flashCount, setFlashCount] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const syncCanScroll = () => {
    const nextCanScroll = contentHeightRef.current > layoutHeightRef.current + 1;
    setCanScroll((current) => (current === nextCanScroll ? current : nextCanScroll));
  };

  const handleOnClosePress = () => {
    Keyboard.dismiss();
    if (props.onClose) {
      props.onClose();
    } else {
      dispatch(lightBoxActions.setVisibility(false));
    }
  };

  const handleOnConfirm = () => {
    Keyboard.dismiss();
    props.onConfirm && props.onConfirm();
  };

  const handleOnCancel = () => {
    Keyboard.dismiss();
    props.onCancel && props.onCancel();
  };

  const handleOnDelete = () => {
    Keyboard.dismiss();
    props.onDelete && props.onDelete();
  };

  useEffect(() => {
    if (!canScroll || flashCount >= 3) {
      return;
    }

    const timeout = setTimeout(() => {
      if (scrollViewRef.current) {
        scrollViewRef.current.flashScrollIndicators();
        setFlashCount((prevCount) => prevCount + 1);
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [canScroll, flashCount]);

  return (
    <ScrollView
      ref={scrollViewRef}
      keyboardShouldPersistTaps={'handled'}
      onLayout={(event) => {
        layoutHeightRef.current = event.nativeEvent.layout.height;
        syncCanScroll();
      }}
      onContentSizeChange={(_, height) => {
        contentHeightRef.current = height;
        syncCanScroll();
      }}
      contentContainerStyle={{ flexGrow: 1, minHeight: props.height }}
      scrollEnabled={canScroll}
      nestedScrollEnabled={canScroll}
      showsVerticalScrollIndicator={canScroll}
      bounces={canScroll}
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      overScrollMode="never"
      style={{
        position: 'relative',
        width: Platform.OS === 'web' ? undefined : 340,
        minWidth: 350,
        maxWidth: props.width ? props.width : Platform.OS === 'web' ? 600 : 350,
        maxHeight: '100%',
        backgroundColor: theme.background.modal,
        borderRadius: 8,
        overflow: 'hidden',
        flexGrow: 0,
      }}
    >
      {props.loading && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2000,
            backgroundColor: isDarkMode ? 'rgba(24, 25, 26, 0.85)' : 'rgba(233, 245, 243, 0.8)',
          }}
        >
          <Loading style={StyleSheet.absoluteFillObject} />
        </View>
      )}
      <Image
        style={{
          position: 'absolute',
          top: -1,
          right: isWeb ? -1 : -9,
          width: 116,
          height: 102,
        }}
        source={require('../../../../assets/questionBubble.png')}
      />
      {!props.hideCloseButton && (
        <Pressable style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }} onPress={handleOnClosePress}>
          <FontAwesomeIcon icon={faXmark} size={20} style={{ color: isDarkMode ? theme.text.primary?.toString() ?? '#ffffff' : 'white' }} />
        </Pressable>
      )}
      <View style={{ paddingTop: 16, paddingHorizontal: 16, paddingBottom: 24, flexGrow: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', width: 220 }}>
          <Label style={{ maxWidth: 185, fontSize: 18, fontWeight: '600', color: theme.text.darkTurquoiseTwo, flexShrink: 1 }} numberOfLines={5}>
            {props.title}
          </Label>
          {props.titleInfoButton && <View style={{ marginLeft: 8, flexShrink: 0 }}>{props.titleInfoButton}</View>}
        </View>
        {props.subtitle != undefined && (
          <Label style={{ width: 220, fontSize: 14, marginTop: 16 }} numberOfLines={5}>
            {props.subtitle}
          </Label>
        )}
        <View style={{ flexGrow: 1, marginTop: 16 }}>{props.children}</View>
        <View style={{ marginTop: 16, marginBottom: 8, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
          {props.showCancel && (
            <Pressable
              style={{
                paddingHorizontal: 12,
                paddingVertical: 12,
                borderRadius: 100,
                borderColor: cancelButtonColor,
                borderWidth: 2,
                backgroundColor: isDarkMode ? 'rgba(95, 200, 186, 0.08)' : 'transparent',
                marginRight: 12,
              }}
              onPress={handleOnCancel}
            >
              <Label style={{ fontSize: 14, fontWeight: '600', color: cancelButtonColor }}>{props.cancelText ?? t('cancel')}</Label>
            </Pressable>
          )}
          {props.showDelete && (
            <Pressable
              style={{
                paddingHorizontal: 12,
                paddingVertical: 12,
                borderRadius: 100,
                backgroundColor: theme.colorErrorPrimary,
                marginRight: 12,
              }}
              onPress={handleOnDelete}
            >
              <Label style={{ fontSize: 14, fontWeight: '600', color: 'white' }}>{t('delete')}</Label>
            </Pressable>
          )}
          {!props.hideConfirm && (
            <Pressable
              style={{
                paddingHorizontal: props.showCancel ? 12 : 20,
                paddingVertical: 12,
                borderRadius: 100,
                backgroundColor: theme.background.darkTurquoise,
              }}
              onPress={handleOnConfirm}
            >
              <Label style={{ fontSize: 14, fontWeight: '600', color: 'white' }}>{props.confirmText ?? t('confirm')}</Label>
            </Pressable>
          )}
        </View>
        <View>
          {props.maxPageNumber != undefined && props.pageNumber != undefined && (
            <Label style={{ fontSize: 14, fontWeight: '600', color: theme.text.darkTurquoiseTwo, position: 'absolute', bottom: 0, left: 0 }}>
              {props.pageNumber}/{props.maxPageNumber}
            </Label>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

export default ProfileModal;
